import { i18n } from "../internationalize/i18n.service"
import { AuthService } from "../auth/services/auth.service"
import type { PushService, PushStatus } from "./push.service"

import "./push.styles.scss"

const COPY = {
  en: {
    title: "Push notifications",
    desc: "Get alerted on this device when we publish a new article, briefing, report or event.",
    enable: "Enable notifications",
    enabled: "Notifications enabled",
    disable: "Disable",
    denied: "Notifications are blocked in your browser settings. Re-enable them there, then reload.",
    unsupported: "Your browser doesn't support push notifications here.",
    ios: "On iPhone/iPad: add this app to your Home Screen first, then open it from the icon to enable notifications.",
    error: "Something went wrong. Please try again.",
  },
  fr: {
    title: "Notifications push",
    desc: "Soyez alerté sur cet appareil dès qu'on publie un nouvel article, briefing, rapport ou événement.",
    enable: "Activer les notifications",
    enabled: "Notifications activées",
    disable: "Désactiver",
    denied: "Les notifications sont bloquées dans les réglages de votre navigateur. Réactivez-les puis rechargez.",
    unsupported: "Votre navigateur ne supporte pas les notifications push ici.",
    ios: "Sur iPhone/iPad : ajoutez d'abord l'app à l'écran d'accueil, puis ouvrez-la depuis l'icône pour activer les notifications.",
    error: "Une erreur est survenue. Réessayez.",
  },
}

export class PushToggleComponent extends HTMLElement {
  private authService: AuthService = window.tbwServices.authService
  private pushService: PushService = window.tbwServices.pushService
  private busy = false

  private get t() { return COPY[i18n.getLanguage() === "fr" ? "fr" : "en"] }

  async connectedCallback() {
    if (!this.authService.isAuthenticated) { this.remove(); return }
    const status = await this.pushService.getStatus()
    this.renderStatus(status)
  }

  private renderStatus(status: PushStatus) {
    const t = this.t
    const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent)

    if (status === "unsupported") {
      this.innerHTML = this.shell(`<p class="tbw-push__hint">${isIOS ? t.ios : t.unsupported}</p>`)
      return
    }
    if (status === "denied") {
      this.innerHTML = this.shell(`<p class="tbw-push__hint tbw-push__hint--warn">${t.denied}</p>`)
      return
    }
    if (status === "subscribed") {
      this.innerHTML = this.shell(`
        <div class="tbw-push__row">
          <span class="tbw-push__badge">✓ ${t.enabled}</span>
          <button class="tbw-push__btn tbw-push__btn--ghost js-disable">${t.disable}</button>
        </div>`)
      this.querySelector(".js-disable")?.addEventListener("click", () => this.onDisable())
      return
    }
    // idle
    this.innerHTML = this.shell(`<button class="tbw-push__btn js-enable">${t.enable}</button>`)
    this.querySelector(".js-enable")?.addEventListener("click", () => this.onEnable())
  }

  private shell(action: string): string {
    return `
      <section class="tbw-push">
        <h2 class="tbw-push__title">${this.t.title}</h2>
        <p class="tbw-push__desc">${this.t.desc}</p>
        ${action}
        <p class="tbw-push__msg js-msg" hidden></p>
      </section>`
  }

  private async onEnable() {
    if (this.busy) return
    this.busy = true
    this.setBusy(".js-enable", true)
    try {
      const status = await this.pushService.enable()
      this.renderStatus(status)
    } catch {
      this.showMsg(this.t.error)
    } finally { this.busy = false }
  }

  private async onDisable() {
    if (this.busy) return
    this.busy = true
    this.setBusy(".js-disable", true)
    try {
      const status = await this.pushService.disable()
      this.renderStatus(status)
    } catch {
      this.showMsg(this.t.error)
    } finally { this.busy = false }
  }

  private setBusy(sel: string, busy: boolean) {
    const btn = this.querySelector<HTMLButtonElement>(sel)
    if (btn) { btn.disabled = busy; btn.style.opacity = busy ? "0.6" : "1" }
  }

  private showMsg(text: string) {
    const el = this.querySelector<HTMLElement>(".js-msg")
    if (el) { el.textContent = text; el.hidden = false }
  }
}
